import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  List,
  Divider
} from '@mui/material';
import {
  Person,
  Schedule,
  CheckCircle,
  LocalCafe,
  Restaurant,
  AccessTime,
  Cancel as CancelIcon,
  Done
} from '@mui/icons-material';
import { useOrders } from '../context/OrdersContext';
import { formatMoscowTime, parseUTCTime } from '../utils/timeUtils';
import { orderDisplayNumber } from '../utils/orderDisplay';

const OrderCard = ({ order, onClick }) => {
  const { confirmOrder, completeOrder, updateOrderStatus } = useOrders();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isUrgent, setIsUrgent] = useState(false);

  const calculateTimeRemaining = useCallback(() => {
    if (!order.estimated_ready_time) return null;

    const now = new Date();
    const readyTime = parseUTCTime(order.estimated_ready_time);

    if (!readyTime) return null;

    const diffMs = readyTime - now;

    if (order.id && diffMs < 0) {
      console.log(`[OrderCard #${order.id}] Время расчета:`, {
        estimated_ready_time: order.estimated_ready_time,
        readyTime: readyTime.toISOString(),
        now: now.toISOString(),
        diffSeconds: Math.floor(diffMs / 1000)
      });
    }

    if (diffMs <= 0) {
      return { total: 0, minutes: 0, seconds: 0, expired: true };
    }

    const totalSeconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return { total: totalSeconds, minutes, seconds, expired: false };
  }, [order.estimated_ready_time, order.id]);

  // Обновление таймера каждую секунду
  useEffect(() => {
    if (!order.estimated_ready_time) return;
    
    // Не показываем таймер для завершенных/отмененных заказов
    if (order.status === 'ready' || order.status === 'completed' || order.status === 'cancelled') {
      setIsUrgent(false);
      return;
    }

    const updateTimer = () => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      
      // Проверяем, осталось ли 5 минут или меньше (только для активных заказов)
      if (remaining && remaining.total > 0 && remaining.total <= 300) {
        setIsUrgent(true);
      } else {
        setIsUrgent(false);
      }
    };

    // Первоначальное обновление
    updateTimer();

    // Обновляем каждую секунду
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeRemaining, order.estimated_ready_time, order.status]);

  // Форматирование времени для отображения
  const formatTimeRemaining = () => {
    if (!timeRemaining) return null;
    
    if (timeRemaining.expired) {
      return <span style={{ color: '#f44336', fontWeight: 'bold' }}>Просрочен!</span>;
    }
    
    const { minutes, seconds } = timeRemaining;
    const timeColor = isUrgent ? '#f44336' : 'inherit';
    
    return (
      <span style={{ color: timeColor, fontWeight: isUrgent ? 'bold' : 'normal' }}>
        {minutes}:{seconds.toString().padStart(2, '0')}
      </span>
    );
  };

  // Получение времени завершения заказа
  const getCompletionTime = () => {
    // Проверяем различные поля времени завершения
    const completionTime = order.completed_at || order.ready_at || order.updated_at;
    if (!completionTime) return null;
    
    return formatTime(completionTime);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
      case 'new':
        return 'error';
      case 'paid':
        return 'info'; // Используем info для кастомного цвета
      case 'awaiting_confirmation':
        return 'warning';
      case 'confirmed':
      case 'preparing':
        return 'warning';
      case 'ready':
        return 'success';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
      case 'new':
        return 'Новый';
      case 'paid':
        return 'Оплачен';
      case 'awaiting_confirmation':
        return 'Ожидает ответа';
      case 'confirmed':
        return 'Ожидает оплаты';
      case 'preparing':
        return 'Готовится';
      case 'ready':
        return 'Готов';
      case 'completed':
        return 'Выполнен';
      case 'cancelled':
        return 'Отменен';
      default:
        return status;
    }
  };

  // Используем утилиту для форматирования московского времени
  const formatTime = formatMoscowTime;

  const handleConfirm = async (e) => {
    e.stopPropagation(); // Предотвращаем открытие модального окна
    try {
      await confirmOrder(order.id);
    } catch (error) {
      console.error('Ошибка подтверждения заказа:', error);
    }
  };

  const handleComplete = async (e) => {
    e.stopPropagation(); // Предотвращаем открытие модального окна
    try {
      await completeOrder(order.id);
    } catch (error) {
      console.error('Ошибка завершения заказа:', error);
    }
  };

  const handleMarkAsCompleted = async (e) => {
    e.stopPropagation(); // Предотвращаем открытие модального окна
    try {
      await updateOrderStatus(order.id, 'completed');
    } catch (error) {
      console.error('Ошибка отметки заказа как выданного:', error);
    }
  };

  const handleStartPreparing = async (e) => {
    e.stopPropagation(); // Предотвращаем открытие модального окна
    try {
      await updateOrderStatus(order.id, 'preparing');
    } catch (error) {
      console.error('Ошибка изменения статуса заказа:', error);
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        cursor: 'pointer',
        border: isUrgent ? '2px solid transparent' : 'none',
        animation: isUrgent ? 'pulse 2s ease-in-out infinite' : 'none',
        '@keyframes pulse': {
          '0%': {
            borderColor: '#f44336',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)'
          },
          '50%': {
            borderColor: '#ff9800',
            boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)'
          },
          '100%': {
            borderColor: '#f44336',
            boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)'
          }
        },
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Chip
            label={getStatusText(order.status)}
            color={order.status === 'paid' || order.status === 'preparing' ? undefined : getStatusColor(order.status)}
            size="small"
            sx={
              order.status === 'paid' ? {
                bgcolor: 'rgb(31, 185, 196)',
                color: 'white',
                fontWeight: 'bold'
              } : order.status === 'preparing' ? {
                bgcolor: 'rgb(33, 150, 243)',
                color: 'white',
                fontWeight: 'bold'
              } : {}
            }
          />
          <Typography variant="caption" color="text.secondary">
            #{orderDisplayNumber(order)}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" mb={2}>
          <Person sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="div">
            Заказ #{orderDisplayNumber(order)}
          </Typography>
        </Box>

        {order.estimated_ready_time && (
          <Box mb={2}>
            <Box display="flex" alignItems="center" mb={0.5}>
              <Schedule sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                Готовность к: <strong>{formatTime(order.estimated_ready_time)}</strong>
              </Typography>
            </Box>
            {/* Показываем таймер обратного отсчета для активных заказов */}
            {timeRemaining && order.status !== 'ready' && order.status !== 'completed' && order.status !== 'cancelled' && (
              <Box display="flex" alignItems="center" ml={0.5}>
                <AccessTime sx={{ mr: 0.5, fontSize: 18, color: isUrgent ? 'error.main' : 'text.secondary' }} />
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  Осталось: {formatTimeRemaining()}
                </Typography>
              </Box>
            )}
            {/* Показываем время завершения для готовых заказов */}
            {(order.status === 'ready' || order.status === 'completed') && getCompletionTime() && (
              <Box display="flex" alignItems="center" ml={0.5}>
                <CheckCircle sx={{ mr: 0.5, fontSize: 18, color: 'success.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                  Приготовлен в: <strong>{getCompletionTime()}</strong>
                </Typography>
              </Box>
            )}
            {/* Показываем время отмены для отмененных заказов */}
            {order.status === 'cancelled' && getCompletionTime() && (
              <Box display="flex" alignItems="center" ml={0.5}>
                <CancelIcon sx={{ mr: 0.5, fontSize: 18, color: 'error.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 'medium', color: 'error.main' }}>
                  Отменен в: <strong>{getCompletionTime()}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ flexGrow: 1, mb: 2 }}>
          <Box display="flex" alignItems="center" mb={1}>
            <LocalCafe sx={{ mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle2">
              Состав заказа:
            </Typography>
          </Box>
          <List dense disablePadding>
            {order.items && order.items.map((item, index) => {
              const itemName = item.item_name || item.name || 'Позиция';
              const sizeName = item.size_name ? ` (${item.size_name})` : '';
              const hasAddons = item.addons && item.addons.length > 0;
              
              return (
                <Box key={item.id || index} sx={{ mb: 1.5 }}>
                  {/* Основная позиция */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="body2">
                      {itemName}{sizeName} × {item.quantity}
                    </Typography>
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {item.price_at_time || 0} ₽
                    </Typography>
                  </Box>
                  
                  {/* Добавки */}
                  {hasAddons && (
                    <Box sx={{ ml: 1.5, mt: 0.5 }}>
                      {item.addons.map((addon, addonIndex) => (
                        <Box 
                          key={addonIndex} 
                          display="flex" 
                          justifyContent="space-between"
                          sx={{ mb: 0.25 }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            + {addon.addon_name || addon.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            +{addon.price_at_time || 0} ₽
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </List>

          {/* Скидка (промокод) */}
          {order.discount_amount != null && Number(order.discount_amount) > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1, py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Скидка (промокод)
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                −{Number(order.discount_amount).toFixed(2)} ₽
              </Typography>
            </Box>
          )}
          {order.loyalty_discount_amount != null && Number(order.loyalty_discount_amount) > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1, py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Скидка (лояльность
                {order.loyalty_level_at_order != null ? `, ур. ${order.loyalty_level_at_order}` : ''})
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                −{Number(order.loyalty_discount_amount).toFixed(2)} ₽
              </Typography>
            </Box>
          )}
          {order.loyalty_bonus_spent != null && Number(order.loyalty_bonus_spent) > 0 && (
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 1, py: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Списание баллов
              </Typography>
              <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                −{Number(order.loyalty_bonus_spent).toFixed(2)} ₽
              </Typography>
            </Box>
          )}
        </Box>

        <Box display="flex" gap={1} mt="auto">
          {(order.status === 'new' || order.status === 'pending') && (
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleConfirm}
              startIcon={<CheckCircle />}
            >
              Принять
            </Button>
          )}
          
          {order.status === 'paid' && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleStartPreparing}
              startIcon={<Restaurant />}
              sx={{
                bgcolor: 'rgb(33, 150, 243)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgb(25, 118, 210)'
                }
              }}
            >
              Готовится
            </Button>
          )}
          
          {order.status === 'preparing' && (
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={handleComplete}
              startIcon={<CheckCircle />}
            >
              Готово
            </Button>
          )}

          {order.status === 'ready' && (
            <Button
              fullWidth
              variant="contained"
              color="success"
              onClick={handleMarkAsCompleted}
              startIcon={<Done />}
            >
              Выдан
            </Button>
          )}

          {order.status === 'completed' && (
            <Typography
              variant="body2"
              color="info.main"
              sx={{ 
                width: '100%',
                textAlign: 'center',
                fontWeight: 'bold',
                py: 1
              }}
            >
              ✓ Заказ выполнен
            </Typography>
          )}

          {order.status === 'cancelled' && (
            <Typography
              variant="body2"
              color="error.main"
              sx={{ 
                width: '100%',
                textAlign: 'center',
                fontWeight: 'bold',
                py: 1
              }}
            >
              ✕ Заказ отменен клиентом
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default OrderCard;

